import { classNames } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useRef } from 'react'
import List from './List'

function lookupDropdown (
  {
    id,
    className,
    suggestions,
    disabledItemsIndices,
    isOpen,
    value,
    selected,
    onClick,
    getSuggestionValue,
    renderNoResultsMessage,
    renderSuggestion,
    suggestionClassName,
    suggestionProps,
    onGroupLabelClick,
    onHover,
    listProps,
    withGrouping,
    enableVirtualScroll
  },
  ref
) {
  const scrollingParentRef = useRef()
  const getGroupChildren = useCallback(({ group }) => group, [])
  const renderGroupLabel = useCallback(({ label }) => label, [])
  const isItemDisabled = useCallback(
    index => disabledItemsIndices.indexOf(index) !== -1,
    [disabledItemsIndices]
  )
  const renderItem = useCallback(
    (suggestion, index) =>
      renderSuggestion(suggestion, { query: value, getSuggestionValue }, index),
    [renderSuggestion, value, getSuggestionValue]
  )
  const itemClassName = useCallback(
    (suggestion, index) => suggestionClassName(suggestion, { query: value, getSuggestionValue }, index),
    [value, suggestionClassName, getSuggestionValue]
  )
  const itemProps = useCallback(
    (suggestion, index) => suggestionProps(suggestion, { query: value, getSuggestionValue }, index),
    [value, suggestionProps, getSuggestionValue]
  )

  return (
    <div
      id={id}
      className={classNames(
        'zb-dropdown-container',
        className
      )}
      ref={ref}
      tabIndex='-1'
    >
      <List
        data={renderNoResultsMessage || !isOpen ? [] : suggestions}
        ref={scrollingParentRef}
        dropdownElementRef={ref}
        dropdownListId={id}
        focusedItemIndex={isOpen ? selected : -1}
        getGroupChildren={getGroupChildren}
        isItemDisabled={isItemDisabled}
        itemClassName={itemClassName}
        itemProps={itemProps}
        onGroupLabelClick={onGroupLabelClick}
        onItemClick={onClick}
        onMouseOver={onHover}
        renderGroupLabel={renderGroupLabel}
        renderItem={renderItem}
        withGrouping={withGrouping}
        enableVirtualScroll={enableVirtualScroll}
        {...listProps}
      >
        {renderNoResultsMessage
          ? (
            <li className='zb-dropdown-list-item'>
              {renderNoResultsMessage({ value })}
            </li>
            )
          : null}
      </List>
    </div>
  )
}

const LookupDropdown = forwardRef(lookupDropdown)

LookupDropdown.displayName = 'LookupDropdown'

LookupDropdown.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  suggestions: PropTypes.array.isRequired,
  disabledItemsIndices: PropTypes.array.isRequired,
  isOpen: PropTypes.bool,
  value: PropTypes.string,
  selected: PropTypes.number,
  onClick: PropTypes.func,
  getSuggestionValue: PropTypes.func,
  renderSuggestion: PropTypes.func,
  suggestionClassName: PropTypes.func,
  suggestionProps: PropTypes.func,
  withGrouping: PropTypes.bool,
  onHover: PropTypes.func,
  enableVirtualScroll: PropTypes.bool
}

export default LookupDropdown
